import React from 'react';
import { useTheme } from '../../theme';
import { BorderStyle, Color, FontWeight } from '../../theme/tokens';
import { CSSRulesFunction } from '../../theme/types';
import { omit } from '../../utils';
import { Text } from '../typography';

type HTMLProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  keyof AppearanceProps & 'children'
>;

export enum BadgeVariant {
  Default = 'Default',
  Actionable = 'Actionable',
  Confirmation = 'Confirmation',
  Error = 'Error',
}

interface AppearanceProps {
  variant?: BadgeVariant;
  radius?: number;
  uppercase?: boolean;
  actionable?: boolean;
}

const badgeColor: Record<
  BadgeVariant,
  { backgroundColor: Color; color: Color }
> = {
  Default: { backgroundColor: Color.ColdGrey_100, color: Color.ColdGrey_800 },
  Actionable: {
    backgroundColor: Color.Secondary_100,
    color: Color.Secondary_800,
  },
  Confirmation: {
    backgroundColor: Color.Success_100,
    color: Color.Success_800,
  },
  Error: { backgroundColor: Color.Error_100, color: Color.Error_800 },
};

export interface BadgeProps extends AppearanceProps, HTMLProps {}

const badgeStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  const {
    variant = BadgeVariant.Default,
    radius = theme.tokens.borderScale * 2,
    actionable,
  } = props;
  const { backgroundColor, color } = badgeColor[variant];

  const actionableStyle = {
    '&:hover': {
      ...theme.border.style(BorderStyle.Solid),
      ...theme.border.color(color),
      ...theme.border.width(1),
      // Need to update border to be inside
      marginTop: '-1px',
      marginLeft: '-1px',
    },
  };

  return [
    {
      display: 'inline-block',
      padding: '4px 12px',
      backgroundColor: theme.color(backgroundColor),
      color: theme.color(color),
      borderRadius: radius,
      hover: {
        backgroundColor: theme.color(Color.Transparent),
        color: theme.color(Color.Inherit),
      },
    },
    actionable ? actionableStyle : undefined,
  ];
};

const Badge: React.FC<BadgeProps> = React.memo(function Badge(props) {
  const { theme } = useTheme();
  const rest = omit(props, [
    'variant',
    'radius',
    'uppercase',
    'actionable',
    'children',
  ]);

  return (
    <div css={badgeStyle(theme, props)} {...rest}>
      <Text fontWeight={FontWeight.SemiBold} uppercase={props.uppercase}>
        {props.children}
      </Text>
    </div>
  );
});

export default Badge;
